import React from "react";
import Avatar from "@material-ui/core/Avatar";
import { makeStyles } from "@material-ui/core/styles";
import Icon from "./icons";
import CompletenessRing from "./CompletenessRing";
import { T, panelShadow } from "./tokens";

const useStyles = makeStyles({
  actionBtn: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: "100%",
    background: "rgba(255,255,255,.08)",
    border: "1.5px solid rgba(255,255,255,.22)",
    color: "#fff",
    borderRadius: 10,
    padding: "11px 14px",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 13,
    textAlign: "left",
    transition: "background .15s",
    "&:hover": { background: "rgba(255,255,255,.16)" },
  },
});

export interface ProfilePanelProps {
  name: string;
  initials: string;
  accountId: string;
  tierLabel: string;
  pct: number;
  statusLabel: string;
  /** A profile-update review is pending admin approval. */
  updatePending?: boolean;
  canUpdateInfo?: boolean;
  onUpdateInfo: () => void;
  onChangePassword: () => void;
}

const ProfilePanel: React.FC<ProfilePanelProps> = ({
  name,
  initials,
  accountId,
  tierLabel,
  pct,
  statusLabel,
  updatePending,
  canUpdateInfo,
  onUpdateInfo,
  onChangePassword,
}) => {
  const classes = useStyles();
  return (
    <div
      style={{
        width: 300,
        flexShrink: 0,
        background: `linear-gradient(165deg, ${T.greenDeep} 0%, ${T.green} 100%)`,
        borderRadius: 20,
        padding: "28px 24px",
        color: "#fff",
        boxShadow: panelShadow,
        position: "sticky",
        top: 20,
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
        <Avatar
          style={{
            width: 84,
            height: 84,
            border: `3px solid ${T.gold}`,
            background: "rgba(255,255,255,.12)",
            color: "#fff",
            fontWeight: 700,
            fontSize: 28,
          }}
        >
          {initials}
        </Avatar>
        <div style={{ fontWeight: 700, fontSize: 19, margin: "14px 0 4px" }}>{name}</div>
        <div style={{ fontWeight: 500, fontSize: 11.5, color: "rgba(255,255,255,.7)" }}>
          {accountId}
        </div>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontWeight: 600,
            fontSize: 11,
            color: T.goldLight,
            background: "rgba(200,147,42,.22)",
            border: "1px solid rgba(200,147,42,.55)",
            padding: "4px 13px",
            borderRadius: 20,
            marginTop: 12,
          }}
        >
          <Icon name="award" size={13} color={T.goldLight} />
          {tierLabel}
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "center", margin: "24px 0 20px" }}>
        <CompletenessRing pct={pct} />
      </div>

      <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            fontWeight: 600,
            fontSize: 12,
            color: "#fff",
            background: "rgba(200,147,42,.28)",
            border: "1px solid rgba(200,147,42,.6)",
            padding: "6px 16px",
            borderRadius: 20,
          }}
        >
          {statusLabel}
        </span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
        {canUpdateInfo && (
          <button type="button" className={classes.actionBtn} onClick={onUpdateInfo}>
            <Icon name="pencil" size={17} color="#fff" />
            Update Information
            {updatePending && (
              <span
                style={{
                  marginLeft: "auto",
                  fontWeight: 600,
                  fontSize: 10.5,
                  color: "#fff",
                  background: "rgba(255,255,255,.18)",
                  border: "1px solid rgba(255,255,255,.28)",
                  padding: "1px 8px",
                  borderRadius: 20,
                }}
              >
                In review
              </span>
            )}
          </button>
        )}
        <button type="button" className={classes.actionBtn} onClick={onChangePassword}>
          <Icon name="lock" size={17} color="#fff" />
          Change Password
        </button>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 9,
          marginTop: 20,
          paddingTop: 18,
          borderTop: "1px solid rgba(255,255,255,.14)",
          fontSize: 11,
          lineHeight: 1.4,
          color: "rgba(255,255,255,.6)",
        }}
      >
        <Icon name="shieldCheck" size={18} color="rgba(255,255,255,.7)" strokeWidth={1.6} />
        SEC-registered &amp; insured. Your data is protected.
      </div>
    </div>
  );
};

export default ProfilePanel;
